/**
 * Хаб «Бюджет сезона {год}» — полноценный экран входа в разделы бюджета.
 *
 * Эталон — макет `v2-00-hub.html` (annual-planning, ветка
 * `docs/wireframes-season-planning-v2`). Решение founder 20.08 (design-pass):
 * «сейчас там попап, а по прототипам там был крутой экран» — Dialog «{сезон} ·
 * разделы» из хаба `/planning` (AK-633) снят, клик по сезону ведёт СЮДА.
 *
 * Из макета реализовано то, под что уже есть данные:
 *   • статус-полоса: вычисляемый статус сезона (канон v3 §5) + рамка дат +
 *     статус бюджета + хозяйства + дедлайн защиты;
 *   • лента согласования бюджета (Планируется → Запланирован → Пересмотрен →
 *     Завершён) с подсветкой текущего;
 *   • «Бюджетные справочники сезона» — разделы с живыми счётчиками позиций
 *     (кэш общий с экраном справочников) и входом;
 *   • «Сборка бюджета» — нумерованные шаги от справочников к производственной
 *     программе: готовые — входы (в т.ч. выполнимость, сравнение и
 *     утверждение), будущие — «скоро» с подписью вехи.
 *
 * Цели от УК (ADR-069) — вход шагом 1 «Сборки бюджета» на экран «Цели
 * сезона»; блок с цифрами целей на самом хабе приедет своей вехой. Гектары
 * пашни и парк техники — выдуманных цифр не рисуем (вердикты head-web о
 * ложных фактах на UAT), блоки приедут своими вехами.
 *
 * Гейты сезона — те же, что у соседей по разделу (`useSeasonByYear`, AK-648):
 * год вне окна → `SeasonYearOutOfWindowCard`, сезон не открыт →
 * `SeasonNotStartedCard` (после открытия экран оживает на месте — карточки
 * сезонов перечитываются инвалидацией).
 */
import { type ReactElement, type ReactNode } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  Banknote,
  BookOpen,
  ClipboardCheck,
  ClipboardList,
  Gauge,
  GitCompare,
  ListChecks,
  PieChart,
  Sprout,
  Target,
  Tractor,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { useCan } from '@/lib/auth/usePermissions';
import { formatRuDate } from '@/lib/dateFmt';
import {
  PLAN_STATUS_TITLES,
  seasonCardStatus,
  type SeasonCard,
  type SeasonPlanStatus,
} from '@/lib/planning/api';
import {
  SECTION_TITLES,
  TMC_SECTIONS,
  useBudgetSectionCounts,
  type BudgetSection,
} from '@/lib/planning/budgetCatalog';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import {
  SEASON_STATUS_BADGE,
  SEASON_STATUS_TITLES,
} from '@/lib/season/computedStatus';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';

/** «0 из 3 утвердили» — короткое значение для ячейки, подпись даёт `dt`. */
function farmsMetaValue(card: SeasonCard): string {
  if (card.farms_total <= 0) return 'нет в доступе';
  if (card.farms_total === 1) {
    return card.farms_planned >= 1 ? 'программа утверждена' : 'программа не утверждена';
  }
  return `${card.farms_planned} из ${card.farms_total} утвердили`;
}

/** Ячейка «подпись — значение» статус-полосы (v2-00: labeled-колонки). */
function MetaCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm leading-6 text-foreground">{children}</dd>
    </div>
  );
}

/**
 * Статус-полоса бюджета (v2-00, верхняя лента): статус сезона вычисляется и
 * не хранится (ADR-060 §S8), статус бюджета — отдельная ось.
 */
function StatusStrip({ card }: { card: SeasonCard }) {
  const seasonStatus = seasonCardStatus(card);
  return (
    <Card>
      <CardContent className="p-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
          <MetaCell label="Статус сезона (вычисляется, не хранится)">
            <Badge variant={SEASON_STATUS_BADGE[seasonStatus]}>
              {SEASON_STATUS_TITLES[seasonStatus]}
            </Badge>
          </MetaCell>
          <MetaCell label="Рамка сезона">
            {card.starts_on && card.ends_on
              ? `${formatRuDate(card.starts_on)} — ${formatRuDate(card.ends_on)}`
              : `год ${card.year}`}
          </MetaCell>
          <MetaCell label="Статус бюджета">
            {card.status_title || PLAN_STATUS_TITLES[card.status]}
          </MetaCell>
          <MetaCell label="Хозяйства">{farmsMetaValue(card)}</MetaCell>
          {card.plan_due_on && (
            <MetaCell label="Дедлайн защиты">{formatRuDate(card.plan_due_on)}</MetaCell>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}

/**
 * Лента согласования бюджета (v2-00, низ «лестницы»): статусы начинаются на
 * согласовании, подготовка идёт гейтами-галочками в «Сборке бюджета» ниже.
 * `archived` в ленту не входит — это полка, а не этап пути.
 */
const STATUS_LADDER: SeasonPlanStatus[] = ['planning', 'planned', 'revised', 'completed'];

function StatusLadder({ card }: { card: SeasonCard }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {STATUS_LADDER.map((status, i) => (
        <span key={status} className="flex items-center gap-2">
          {i > 0 && <span aria-hidden className="text-muted-foreground">→</span>}
          <span
            className={
              status === card.status
                ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 font-medium text-primary'
                : 'rounded-full border border-border px-3 py-1 text-muted-foreground'
            }
          >
            {PLAN_STATUS_TITLES[status]}
          </span>
        </span>
      ))}
      <span className="text-muted-foreground">
        после утверждения бюджет = производственная программа {card.year}
      </span>
    </div>
  );
}

/** Плитка раздела справочников: счётчик позиций + вход (кэш общий с экраном). */
function CatalogTile({
  title,
  note,
  count,
  renderLink,
}: {
  title: string;
  note: string;
  /** `null` — у плитки счётчика нет (её раздел живёт не в бюджетном справочнике). */
  count: number | null | undefined;
  renderLink?: (className: string, children: ReactNode) => ReactElement;
}) {
  const body = (
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium text-foreground">{title}</span>
      <span className="mt-1 block text-xs leading-5 text-muted-foreground">{note}</span>
      {/* Счётчик — только у живой плитки: на закрытой (сезон не открыт / нет
          права) «позиции считаются…» читалось бы как вечная загрузка. */}
      {renderLink && count !== null && (
        <span className="mt-1 block text-xs leading-5 text-muted-foreground">
          {count === undefined ? 'позиции считаются…' : `позиций в бюджете: ${count}`}
        </span>
      )}
    </span>
  );
  if (renderLink) {
    return (
      <li className="min-w-0">
        {renderLink(
          'group flex h-full items-start gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/40',
          body,
        )}
      </li>
    );
  }
  return (
    <li className="flex h-full min-w-0 items-start gap-3 rounded-lg border border-dashed border-border bg-muted/40 p-3">
      {body}
    </li>
  );
}

/** Шаг «Сборки бюджета» — нумерованная строка по образцу .steps макета v2-00. */
function BuildStep({
  no,
  icon: Icon,
  title,
  desc,
  state,
  status,
  note,
  renderLink,
}: {
  no: number;
  icon: LucideIcon;
  title: string;
  desc: string;
  state: 'ready' | 'locked' | 'soon';
  /** Правая колонка — честный статус шага, если он известен. */
  status?: string;
  /** Для закрытых — причина, для будущих — веха. */
  note?: string;
  renderLink?: (className: string, children: ReactNode) => ReactElement;
}) {
  const ready = state === 'ready';
  return (
    <li className="flex items-start gap-3 border-b border-border py-3 last:border-b-0">
      <span
        className={
          ready
            ? 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-medium text-foreground'
            : 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground'
        }
      >
        {no}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <Icon className={ready ? 'h-4 w-4 text-primary' : 'h-4 w-4 text-muted-foreground'} />
          {ready && renderLink ? (
            renderLink('text-sm font-medium text-foreground hover:text-primary', <>{title}</>)
          ) : (
            <span className="text-sm font-medium text-muted-foreground">{title}</span>
          )}
          {state === 'soon' && <Badge variant="outline">скоро</Badge>}
        </span>
        <span className="mt-1 block text-xs leading-5 text-muted-foreground">{desc}</span>
        {note && (
          <span className="mt-1 block text-xs leading-5 text-accent-brand">{note}</span>
        )}
      </span>
      {status && (
        <span className="shrink-0 text-xs leading-6 text-muted-foreground">{status}</span>
      )}
    </li>
  );
}

/** Разделы справочников на плитках хаба: ТМЦ — свод пяти категорий. */
// Цен готовой продукции здесь нет (AK-769): они ведутся в «Ценах и объёмах
// реализации» — шаг 3 сборки бюджета ниже. Плитка вела в бюджетный справочник,
// где правка цены до плана по выручке не доезжала.
const CATALOG_FARM_SECTIONS: readonly BudgetSection[] = ['work_rate', 'outsource'];

export function SeasonBudgetHubPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const season = useSeasonByYear(year);
  const card = season.card;
  const seasonId = season.seasonId;

  const canReadBudgetCatalog = useCan('budget_catalog', 'r');
  const canReadTechCards = useCan('tech_cards', 'r');
  // Цели сезона (ADR-069): R есть у всех ролей планирования, но ресурс
  // отдельный — без него юзер дошёл бы до экрана и получил отказ бэка.
  const canReadBudgetTargets = useCan('budget_targets', 'r');

  // Счётчики позиций — только когда есть сезон и право читать справочники:
  // без права запросы дали бы отказ бэка на каждую категорию.
  const countSections =
    seasonId && canReadBudgetCatalog
      ? ([...TMC_SECTIONS, ...CATALOG_FARM_SECTIONS] as readonly BudgetSection[])
      : ([] as readonly BudgetSection[]);
  const { counts } = useBudgetSectionCounts(countSections, {
    seasonId,
    farmId: null,
    includeArchived: false,
  });
  const tmcCount = TMC_SECTIONS.reduce<number | undefined>((acc, s) => {
    const c = counts[s];
    if (c === undefined) return acc;
    return (acc ?? 0) + c;
  }, undefined);

  const yearParam = String(year);
  /** Причина «закрыт»: без сезона — открыть бюджет, с сезоном — нет права. */
  const lockNote = (hasRight: boolean) =>
    !seasonId
      ? 'откроется после открытия бюджета сезона'
      : !hasRight
        ? 'нет доступа — попросите администратора компании'
        : undefined;

  const title =
    card?.name?.trim() || (season.validYear ? `Бюджет сезона ${year}` : 'Бюджет сезона');

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1200px] pb-safe-fab">
        <PageHeader
          title={title}
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            { label: season.validYear ? `Бюджет сезона ${year}` : 'Бюджет сезона' },
          ]}
          description="Бюджет сезона — рабочий контейнер планирования: справочники, техкарты, посевной план, сценарии. После утверждения этот же бюджет называется производственной программой — до утверждения такого имени нет."
        />

        <Stack gap={4} className="mt-4">
          {!season.validYear && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Год в адресе страницы некорректный.
              </CardContent>
            </Card>
          )}

          {season.validYear && season.isLoading && <Skeleton className="h-96 w-full" />}

          {season.validYear && season.isError && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Не удалось загрузить сезоны. Обновите страницу.
              </CardContent>
            </Card>
          )}

          {season.missing && <SeasonYearOutOfWindowCard year={year} />}
          {season.notOpened && (
            // Остаёмся на месте: после открытия инвалидация карточек сезонов
            // перечитает `season_id`, и хаб оживает без перехода.
            <SeasonNotStartedCard year={year} onOpened={() => undefined} />
          )}

          {card && (
            <>
              <StatusStrip card={card} />

              <Card>
                <CardHeader className="p-4 pb-3">
                  <CardTitle className="text-base">Согласование бюджета</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <Stack gap={3}>
                    <StatusLadder card={card} />
                    <p className="text-xs text-muted-foreground">
                      Подготовка идёт гейтами-галочками (сборка ниже): возвраты и
                      итерации разрешены — цикл «правим техкарту ↔ пересобираем
                      структуру» ничего не ломает.
                    </p>
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-4 pb-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle className="text-base">
                      Бюджетные справочники сезона {card.year}
                    </CardTitle>
                    <Badge variant={card.catalogs_approved >= card.catalogs_total ? 'primary' : 'outline'}>
                      {card.catalogs_approved} из {card.catalogs_total} утверждены
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <Stack gap={3}>
                    <p className="text-sm text-muted-foreground">
                      Бюджетный справочник — не копия справочника хозяйства: у него свой
                      состав, а плановая цена — атрибут позиции бюджета. Правки здесь не
                      меняют справочники хозяйства и не трогают факт прошлых сезонов.
                    </p>
                    <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                      <CatalogTile
                        title="ТМЦ и плановые цены"
                        note="Семена, удобрения, СЗР, ГСМ — цена и вид у позиции бюджета."
                        count={tmcCount}
                        renderLink={
                          seasonId && canReadBudgetCatalog
                            ? (cls, children) => (
                                <Link
                                  aria-label="ТМЦ и плановые цены"
                                  className={cls}
                                  to="/planning/seasons/$year/budget-catalogs"
                                  params={{ year: yearParam }}
                                >
                                  {children}
                                </Link>
                              )
                            : undefined
                        }
                      />
                      <CatalogTile
                        title={SECTION_TITLES.work_rate}
                        note="₽/га своей работы и расход топлива по операциям."
                        count={counts.work_rate}
                        renderLink={
                          seasonId && canReadBudgetCatalog
                            ? (cls, children) => (
                                <Link
                                  aria-label={SECTION_TITLES.work_rate}
                                  className={cls}
                                  to="/planning/seasons/$year/budget-catalogs"
                                  params={{ year: yearParam }}
                                >
                                  {children}
                                </Link>
                              )
                            : undefined
                        }
                      />
                      <CatalogTile
                        title={SECTION_TITLES.outsource}
                        note="Наёмные работы — без техники хозяйства."
                        count={counts.outsource}
                        renderLink={
                          seasonId && canReadBudgetCatalog
                            ? (cls, children) => (
                                <Link
                                  aria-label={SECTION_TITLES.outsource}
                                  className={cls}
                                  to="/planning/seasons/$year/budget-catalogs"
                                  params={{ year: yearParam }}
                                >
                                  {children}
                                </Link>
                              )
                            : undefined
                        }
                      />
                      <CatalogTile
                        title="Цены реализации"
                        note="Матрица культура × месяц, ₽/т без НДС — из неё считается выручка."
                        count={null}
                        renderLink={
                          seasonId
                            ? (cls, children) => (
                                <Link
                                  aria-label="Цены реализации"
                                  className={cls}
                                  to="/planning/seasons/$year/sale-prices"
                                  params={{ year: yearParam }}
                                >
                                  {children}
                                </Link>
                              )
                            : undefined
                        }
                      />
                    </ul>
                    {!canReadBudgetCatalog && (
                      <p className="text-xs text-accent-brand">
                        Справочники читает тот, кто ведёт планирование, — попросите
                        администратора компании выдать доступ.
                      </p>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-4 pb-3">
                  <CardTitle className="text-base">
                    Сборка бюджета — от справочников к производственной программе
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {/* Этап 0 (UC-0, AK-763) — не шаг сборки, а проверка ПЕРЕД
                      ней, поэтому стоит НАД нумерованным списком, а не первым
                      его пунктом: нумерация шагов бюджета уже разошлась по
                      ссылкам и подписям, а «нулевого шага» в ней нет. */}
                  <Link
                    to="/planning/seasons/$year/readiness"
                    params={{ year: yearParam }}
                    search={{}}
                    aria-label="Готовность данных к планированию"
                    className="mb-4 flex items-start gap-3 rounded-md border border-border bg-muted p-3 transition-colors hover:bg-accent"
                  >
                    <ClipboardCheck className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground">
                        Готовность данных к планированию
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        Чего не хватает, чтобы сесть считать план, и где это починить.
                        Считает система — руками ничего не отмечается.
                      </span>
                    </span>
                  </Link>
                  {/* Нормативы урожайности (AK-789) — тоже НЕ шаг сборки, а
                      справочник, из которого шаги 6-7 берут цифру. В нумерацию
                      его не вставляем по той же причине, что и готовность
                      данных: номера шагов уже разошлись по ссылкам и
                      подписям. Раньше входа к нормативам не было вовсе —
                      норматив жил внутренней ручкой. */}
                  <Link
                    to="/planning/seasons/$year/yield-norms"
                    params={{ year: yearParam }}
                    search={{}}
                    aria-label="Нормативы урожайности"
                    className="mb-4 flex items-start gap-3 rounded-md border border-border bg-muted p-3 transition-colors hover:bg-accent"
                  >
                    <Sprout className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground">
                        Нормативы урожайности
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        Цифра по умолчанию для компании и своя для хозяйства — её
                        подхватывают поля, заведённые без своей урожайности.
                      </span>
                    </span>
                  </Link>
                  <ol>
                    <BuildStep
                      no={1}
                      icon={Target}
                      title="Цели сезона"
                      desc="Целевые выручка и прибыль от управляющей компании: холдинг, кластеры, хозяйства."
                      state={seasonId && canReadBudgetTargets ? 'ready' : 'locked'}
                      note={lockNote(canReadBudgetTargets)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Цели сезона"
                          className={cls}
                          to="/planning/seasons/$year/budget-targets"
                          params={{ year: yearParam }}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    <BuildStep
                      no={2}
                      icon={BookOpen}
                      title="Бюджетные справочники"
                      desc="Состав позиций ТМЦ, плановые цены, расценки работ, цены готовой продукции."
                      state={seasonId && canReadBudgetCatalog ? 'ready' : 'locked'}
                      status={`${card.catalogs_approved} из ${card.catalogs_total} утверждено`}
                      note={lockNote(canReadBudgetCatalog)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Бюджетные справочники"
                          className={cls}
                          to="/planning/seasons/$year/budget-catalogs"
                          params={{ year: yearParam }}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    <BuildStep
                      no={3}
                      icon={Banknote}
                      title="Плановые цены реализации"
                      desc="Матрица культура × месяц, ₽/т без НДС."
                      state="ready"
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Плановые цены реализации"
                          className={cls}
                          to="/planning/seasons/$year/sale-prices"
                          params={{ year: yearParam }}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    <BuildStep
                      no={4}
                      icon={ClipboardList}
                      title="Варианты техкарт по культурам"
                      desc="Атом бюджета: операции с агрономикой, сменные блоки защиты и питания, живой ₽/га."
                      state={seasonId && canReadTechCards ? 'ready' : 'locked'}
                      note={lockNote(canReadTechCards)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Варианты техкарт по культурам"
                          className={cls}
                          to="/planning/seasons/$year/tech-cards"
                          params={{ year: yearParam }}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    <BuildStep
                      no={5}
                      icon={Tractor}
                      title="Парк техники и нормы выработки"
                      desc="Машины и сцепки, выработка и расход топлива по тех.операциям."
                      state="soon"
                      note="появится вехой «Парк техники» (макет v2-12)"
                    />
                    <BuildStep
                      no={6}
                      icon={Sprout}
                      title="Посевной план"
                      desc="На каком поле какая культура; вариантов может быть несколько."
                      state={seasonId ? 'ready' : 'locked'}
                      note={lockNote(true)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Посевной план"
                          className={cls}
                          to="/planning/seasons/$year/sowing-plan"
                          params={{ year: yearParam }}
                          search={{}}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    <BuildStep
                      no={7}
                      icon={ListChecks}
                      title="Назначение техкарт культурам варианта"
                      desc="Культура × урожайность × вариант техкарты — появляется ₽/га."
                      state={seasonId ? 'ready' : 'locked'}
                      note={lockNote(true)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Назначение техкарт культурам варианта"
                          className={cls}
                          to="/planning/seasons/$year/plan-variant"
                          params={{ year: yearParam }}
                          search={{}}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    <BuildStep
                      no={8}
                      icon={PieChart}
                      title="Структура посевных площадей"
                      desc="Свод варианта по культурам: площади, доли от пашни, валовка, севооборот."
                      state={seasonId ? 'ready' : 'locked'}
                      note={lockNote(true)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Структура посевных площадей"
                          className={cls}
                          to="/planning/seasons/$year/crop-structure"
                          params={{ year: yearParam }}
                          search={{}}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    <BuildStep
                      no={9}
                      icon={TrendingUp}
                      title="Сводка варианта"
                      desc="Валовка, выручка, затраты, маржа по варианту плана."
                      state={seasonId ? 'ready' : 'locked'}
                      note={lockNote(true)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Сводка варианта"
                          className={cls}
                          to="/planning/seasons/$year/plan-variant/summary"
                          params={{ year: yearParam }}
                          search={{}}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    {/* Шаги 10-12 ведут на готовые экраны (AK-772): до этого
                        они висели «скоро», и до кнопки «Утвердить» дойти было
                        нельзя — у экрана сравнения не было ни одной входящей
                        ссылки. Хозяйство и вариант в адрес не кладём (как у
                        шагов 6-9): экраны берут хозяйство из выбора, а
                        вариант — «План сезона» или первый. */}
                    <BuildStep
                      no={10}
                      icon={Gauge}
                      title="Выполнимость по технике"
                      desc="Машино-смены парка против агроокон техкарт — успеваем или нет."
                      state={seasonId && canReadTechCards ? 'ready' : 'locked'}
                      note={lockNote(canReadTechCards)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Выполнимость по технике"
                          className={cls}
                          to="/planning/seasons/$year/plan-variant/feasibility"
                          params={{ year: yearParam }}
                          search={{}}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    <BuildStep
                      no={11}
                      icon={GitCompare}
                      title="Сравнение вариантов плана"
                      desc="Варианты бок о бок: себестоимость, выручка, маржа, выполнимость и цели сезона."
                      state={seasonId && canReadTechCards ? 'ready' : 'locked'}
                      note={lockNote(canReadTechCards)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Сравнение вариантов плана"
                          className={cls}
                          to="/planning/seasons/$year/programs/compare"
                          params={{ year: yearParam }}
                          search={{}}
                        >
                          {children}
                        </Link>
                      )}
                    />
                    {/* Утверждают на том же экране, где сравнивают: решение
                        принимается, глядя на соседние варианты. Отдельного
                        экрана утверждения нет — шаг ведёт туда же. */}
                    <BuildStep
                      no={12}
                      icon={ListChecks}
                      title="Утверждение бюджета → производственная программа"
                      desc="Расчёт фиксируется: нормы, цены и суммы на момент утверждения; дальше — только отклонения с причиной."
                      state={seasonId && canReadTechCards ? 'ready' : 'locked'}
                      note={lockNote(canReadTechCards)}
                      renderLink={(cls, children) => (
                        <Link
                          aria-label="Утверждение бюджета"
                          className={cls}
                          to="/planning/seasons/$year/programs/compare"
                          params={{ year: yearParam }}
                          search={{}}
                        >
                          {children}
                        </Link>
                      )}
                    />
                  </ol>
                </CardContent>
              </Card>
            </>
          )}
        </Stack>
      </div>
    </AppShell>
  );
}

export default SeasonBudgetHubPage;
